<template>
  <div>
    <v-dialog
      :value="value"
      max-width="920"
      scrollable
      @input="$emit('input', $event)"
    >
      <v-card class="payment-details-card">
        <v-card-title class="payment-dialog-title px-6 py-5">
          <div class="d-flex align-start">
            <v-avatar color="deep-orange lighten-5" size="44" class="mr-4 flex-shrink-0">
              <v-icon color="#ff5722">mdi-receipt-text-check-outline</v-icon>
            </v-avatar>
            <div>
              <div class="payment-dialog-heading">
                {{ $t("payment.payment_details") }}
              </div>
              <div class="payment-dialog-meta mt-2">
                <v-chip small outlined color="deep-orange darken-1">
                  <v-icon left small>mdi-card-account-details-outline</v-icon>
                  {{ student_id }}
                </v-chip>
                <v-chip small outlined color="deep-orange darken-1">
                  <v-icon left small>mdi-account-outline</v-icon>
                  {{ student_name }}
                </v-chip>
                <v-chip small outlined color="deep-orange darken-1">
                  <v-icon left small>mdi-calendar-month-outline</v-icon>
                  {{ $t("payment.academic_period", period_params) }}
                </v-chip>
              </div>
            </div>
          </div>
        </v-card-title>

        <v-divider />
        <v-card-text class="pt-5">
          <v-alert v-if="load_error" type="error" outlined text>
            {{ translated_error(load_error) }}
          </v-alert>
          <div v-if="loading" class="py-8 text-center">
            <v-progress-circular indeterminate color="#ff5722" />
          </div>

          <template v-else-if="details">
            <v-alert
              v-if="!details.billing.length && !details.holding.length"
              type="info"
              outlined
              text
            >
              {{ $t("payment.no_payment_details") }}
            </v-alert>

            <div v-if="can_create" class="text-right mb-4">
              <v-btn color="#ff5722" dark @click="open_create">
                <v-icon left>mdi-cash-plus</v-icon>
                {{ $t("payment.create_payment_record") }}
              </v-btn>
            </div>

            <section v-if="details.registration || details.billing.length">
              <div class="payment-section-heading mb-3">
                <div class="d-flex align-center">
                  <v-icon color="#ff5722" class="mr-2">mdi-check-decagram-outline</v-icon>
                  <span>{{ $t("payment.registration_and_payment_records") }}</span>
                </div>
                <v-chip small color="deep-orange lighten-5" text-color="#ff5722">
                  <v-icon left x-small>mdi-receipt-text-outline</v-icon>
                  {{
                    $t("payment.receipt_count", {
                      count: details.billing.length,
                    })
                  }}
                </v-chip>
              </div>
              <div class="billing-comparison-wrap">
                <table class="billing-comparison-table">
                  <thead>
                    <tr>
                      <th class="sequence-column">{{ $t("payment.order") }}</th>
                      <th class="fee-name-column">{{ $t("payment.transaction") }}</th>
                      <th
                        v-if="details.registration"
                        class="billing-record-column registration-record-column"
                      >
                        <div class="billing-record-number">
                          {{ registration_type_label(details.registration) }}
                        </div>
                        <div class="billing-receipt-number">
                          {{ $t("payment.registration_information") }}
                        </div>
                      </th>
                      <th
                        v-for="(record, index) in details.billing"
                        :key="`header-${record.bill_no}`"
                        class="billing-record-column"
                      >
                        <div class="billing-record-number">
                          {{
                            $t("payment.payment_record_number", {
                              number: index + 1,
                            })
                          }}
                        </div>
                        <div class="billing-receipt-number">
                          {{ $t("payment.receipt_number") }} {{ record.bill_no }}
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(field, fieldIndex) in details.visible_fee_fields"
                      :key="field"
                    >
                      <td class="sequence-cell">{{ fieldIndex + 1 }}</td>
                      <th class="fee-name-cell">{{ fee_label(field) }}</th>
                      <td
                        v-if="details.registration"
                        class="amount-cell registration-amount"
                        :class="{
                          'zero-amount': Number(details.registration.amounts[field]) === 0,
                        }"
                      >
                        {{ format_amount(details.registration.amounts[field]) }}
                      </td>
                      <td
                        v-for="record in details.billing"
                        :key="`${record.bill_no}-${field}`"
                        class="amount-cell"
                        :class="{ 'zero-amount': Number(record[field]) === 0 }"
                      >
                        {{ format_amount(record[field]) }}
                      </td>
                    </tr>
                    <tr class="billing-total-row">
                      <td />
                      <th class="comparison-summary-label">
                        <div>
                          <v-icon small color="#ff5722">mdi-cash-multiple</v-icon>
                          <span>{{ $t("payment.total") }}</span>
                        </div>
                      </th>
                      <td
                        v-if="details.registration"
                        class="amount-cell registration-total-amount"
                      >
                        {{ format_amount(details.registration.summary) }} {{ $t("payment.baht") }}
                      </td>
                      <td
                        v-for="record in details.billing"
                        :key="`total-${record.bill_no}`"
                        class="amount-cell total-amount"
                      >
                        {{ format_amount(record.summary) }} {{ $t("payment.baht") }}
                      </td>
                    </tr>
                    <tr class="billing-meta-row">
                      <td />
                      <th class="comparison-summary-label">
                        <div>
                          <v-icon small color="#ff5722">mdi-calendar-clock-outline</v-icon>
                          <span>{{ $t("payment.record_date") }}</span>
                        </div>
                      </th>
                      <td v-if="details.registration" class="text-center">
                        {{ format_date(details.registration.date_time) }}
                      </td>
                      <td
                        v-for="record in details.billing"
                        :key="`date-${record.bill_no}`"
                        class="text-center"
                        :class="{ 'billing-date-with-transaction': record.creditcard_datetime }"
                      >
                        <div>{{ format_date(record.pay_date) }}</div>
                        <div v-if="record.creditcard_datetime" class="billing-transaction-date">
                          <div>{{ $t("payment.transaction_date") }} :</div>
                          <div>{{ format_datetime(record.creditcard_datetime) }}</div>
                        </div>
                      </td>
                    </tr>
                    <tr class="billing-bank-row">
                      <td />
                      <th class="comparison-summary-label">
                        <div>
                          <v-icon small color="#ff5722">mdi-bank-outline</v-icon>
                          <span>{{ $t("payment.bank") }}</span>
                        </div>
                      </th>
                      <td v-if="details.registration" class="text-center">-</td>
                      <td
                        v-for="record in details.billing"
                        :key="`bank-${record.bill_no}`"
                        class="text-center font-weight-bold"
                      >
                        {{ record.bank_name || record.bank || "-" }}
                      </td>
                    </tr>
                    <tr
                      v-if="(can_edit || can_delete) && details.billing.length"
                      class="billing-action-row"
                    >
                      <td />
                      <th class="comparison-summary-label">
                        <div>
                          <v-icon small color="#ff5722">mdi-cog-outline</v-icon>
                          <span>{{ $t("payment.actions") }}</span>
                        </div>
                      </th>
                      <td v-if="details.registration" class="text-center">-</td>
                      <td
                        v-for="record in details.billing"
                        :key="`actions-${record.bill_no}`"
                      >
                        <div class="billing-record-actions">
                          <v-btn
                            v-if="can_edit"
                            small
                            outlined
                            color="#ff5722"
                            @click="open_edit(record)"
                          >
                            <v-icon small left>mdi-pencil</v-icon>
                            {{ $t("payment.edit") }}
                          </v-btn>
                          <v-btn
                            v-if="can_delete"
                            small
                            outlined
                            color="error"
                            @click="open_delete(record)"
                          >
                            <v-icon small left>mdi-delete-outline</v-icon>
                            {{ $t("payment.delete") }}
                          </v-btn>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section v-if="details.holding.length" class="mt-6">
              <div class="subtitle-1 font-weight-bold mb-3">
                {{ $t("payment.pending_payment_records") }}
              </div>
              <v-alert type="info" text dense>
                {{ $t("payment.pending_payment_note") }}
              </v-alert>
              <v-simple-table dense class="detail-table">
                <thead>
                  <tr>
                    <th>{{ $t("payment.payment_date_time") }}</th>
                    <th>{{ $t("payment.reference_number") }}</th>
                    <th class="text-right">{{ $t("payment.total_paid") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(record, index) in details.holding" :key="`${record.REF2}-${index}`">
                    <td>{{ format_datetime(record.PAYMENT_DATETIME) }}</td>
                    <td>{{ record.REF2 || "-" }}</td>
                    <td class="text-right">
                      {{ format_amount(record.AMOUNT) }} {{ $t("payment.baht") }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </section>
          </template>
        </v-card-text>

        <v-divider />
        <v-card-actions class="justify-end px-6 py-4">
          <v-btn outlined color="#ff5722" @click="$emit('input', false)">
            {{ $t("payment.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="edit_dialog" max-width="820" persistent>
      <v-card v-if="edit_record" class="payment-entry-card">
        <v-card-title class="payment-dialog-title px-6 py-4">
          <div class="d-flex align-start">
            <v-avatar color="deep-orange lighten-5" size="44" class="mr-4 flex-shrink-0">
              <v-icon color="#ff5722">
                {{ creating ? "mdi-receipt-text-plus-outline" : "mdi-receipt-text-edit-outline" }}
              </v-icon>
            </v-avatar>
            <div>
              <div class="payment-dialog-heading">
                {{ $t(creating ? "payment.create_payment_record" : "payment.edit_payment_record") }}
              </div>
              <div class="payment-dialog-meta mt-2">
                <v-chip small outlined color="deep-orange darken-1">
                  <v-icon left small>mdi-card-account-details-outline</v-icon>
                  {{ student_id }}
                </v-chip>
                <v-chip small outlined color="deep-orange darken-1">
                  <v-icon left small>mdi-account-outline</v-icon>
                  {{ student_name }}
                </v-chip>
                <v-chip small outlined color="deep-orange darken-1">
                  <v-icon left small>mdi-calendar-month-outline</v-icon>
                  {{ $t("payment.academic_period", period_params) }}
                </v-chip>
                <v-chip
                  v-if="!creating"
                  small
                  outlined
                  color="deep-orange darken-1"
                >
                  <v-icon left small>mdi-receipt-text-outline</v-icon>
                  {{ $t("payment.receipt_number") }} {{ edit_record.bill_no }}
                </v-chip>
              </div>
            </div>
          </div>
        </v-card-title>
        <v-divider />
        <v-card-text class="payment-entry-content px-6 py-3">
          <PaymentBillingRecordForm
            v-model="edit_record"
            :fee_fields="details.fee_fields"
            :visible_fee_fields="details.visible_fee_fields"
            :bank_options="bank_options"
            :loading="saving"
            :submit_disabled="!has_changes"
            :submit_label="$t('payment.save')"
            @cancel="close_edit"
            @submit="confirm_dialog = true"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirm_dialog" max-width="760" persistent>
      <v-card>
        <v-card-title class="payment-dialog-title">
          {{ $t(creating ? "payment.confirm_billing_create_title" : "payment.confirm_billing_update_title") }}
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" text dense>
            {{ $t(creating ? "payment.confirm_billing_create_message" : "payment.confirm_billing_update_message") }}
          </v-alert>
          <v-simple-table dense>
            <tbody>
              <tr><th>{{ $t("pre_regis_panel.student_id") }}</th><td>{{ student_id }}</td></tr>
              <tr><th>{{ $t("pre_regis_panel.name_surname") }}</th><td>{{ student_name }}</td></tr>
              <tr><th>{{ $t("payment.academic_period_label") }}</th><td>{{ item.semester }} / {{ item.year }}</td></tr>
              <tr>
                <th>{{ $t("payment.receipt_number") }}</th>
                <td>
                  {{ creating ? $t("payment.automatic_receipt_number") : (edit_record && edit_record.bill_no) }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <div class="subtitle-1 font-weight-bold mt-5 mb-2">
            {{ $t("payment.change_details") }}
          </div>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>{{ $t("payment.transaction") }}</th>
                <th class="text-right">{{ $t("payment.current_value") }}</th>
                <th class="text-right">{{ $t("payment.new_value") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="change in changes" :key="change.field">
                <td>{{ change.label }}</td>
                <td class="text-right">{{ change.old_value }}</td>
                <td class="text-right orange-change">{{ change.new_value }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-card-actions class="justify-end px-6 py-4">
          <v-btn outlined color="#ff5722" :disabled="saving" @click="confirm_dialog = false">
            {{ $t("payment.cancel") }}
          </v-btn>
          <v-btn
            color="#ff5722"
            :dark="has_changes"
            :disabled="!has_changes"
            :loading="saving"
            @click="save_record"
          >
            {{ $t("payment.confirm_and_save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="delete_dialog" max-width="720" persistent>
      <v-card v-if="delete_record">
        <v-card-title class="payment-dialog-title px-6 py-4">
          <div class="d-flex align-center">
            <v-avatar color="red lighten-5" size="42" class="mr-3">
              <v-icon color="error">mdi-delete-alert-outline</v-icon>
            </v-avatar>
            <span class="payment-dialog-heading">
              {{ $t("payment.confirm_billing_delete_title") }}
            </span>
          </div>
        </v-card-title>
        <v-divider />
        <v-card-text class="px-6 pt-5">
          <v-alert type="warning" text dense>
            {{ $t("payment.confirm_billing_delete_message") }}
          </v-alert>
          <v-simple-table dense class="delete-summary-table mb-4">
            <tbody>
              <tr>
                <th>{{ $t("pre_regis_panel.student_id") }}</th>
                <td>{{ student_id }}</td>
              </tr>
              <tr>
                <th>{{ $t("pre_regis_panel.name_surname") }}</th>
                <td>{{ student_name }}</td>
              </tr>
              <tr>
                <th>{{ $t("payment.academic_period_label") }}</th>
                <td>{{ item.semester }} / {{ item.year }}</td>
              </tr>
              <tr>
                <th>{{ $t("payment.receipt_number") }}</th>
                <td>{{ delete_record.bill_no }}</td>
              </tr>
              <tr>
                <th>{{ $t("payment.payment_date") }}</th>
                <td>{{ format_date(delete_record.pay_date) }}</td>
              </tr>
              <tr>
                <th>{{ $t("payment.total_paid") }}</th>
                <td class="font-weight-bold">
                  {{ format_amount(delete_record.summary) }} {{ $t("payment.baht") }}
                </td>
              </tr>
              <tr>
                <th>{{ $t("payment.bank") }}</th>
                <td>{{ delete_record.bank_name || delete_record.bank || "-" }}</td>
              </tr>
            </tbody>
          </v-simple-table>
          <v-textarea
            v-model="delete_reason"
            outlined
            rows="3"
            counter="500"
            :label="$t('payment.delete_reason')"
            :hint="$t('payment.delete_reason_hint')"
            persistent-hint
            :disabled="deleting"
          />
        </v-card-text>
        <v-card-actions class="justify-end px-6 py-4">
          <v-btn outlined color="#ff5722" :disabled="deleting" @click="close_delete">
            {{ $t("payment.cancel") }}
          </v-btn>
          <v-btn
            color="error"
            :dark="delete_reason_valid"
            :disabled="!delete_reason_valid"
            :loading="deleting"
            @click="delete_payment_record"
          >
            <v-icon left>mdi-delete-outline</v-icon>
            {{ $t("payment.delete_payment_record") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import payInSlipProvider from "@/services/pay_in_slip.provider";
import { objectTranslatorLowerCase } from "@/helper/translator";
import PaymentBillingRecordForm from "./PaymentBillingRecordForm.vue";

const payInSlipService = new payInSlipProvider();

export default {
  name: "PaymentBillingDetailsDialog",
  components: {
    PaymentBillingRecordForm,
  },
  props: {
    value: { type: Boolean, default: false },
    item: { type: Object, default: null },
    student_id: { type: String, required: true },
    student_name: { type: String, default: "" },
    level_id: { type: Number, required: true },
    sub_level_id: { type: Number, default: null },
    editable: { type: Boolean, default: false },
  },
  data: () => ({
    loading: false,
    saving: false,
    load_error: null,
    details: null,
    edit_dialog: false,
    confirm_dialog: false,
    delete_dialog: false,
    edit_record: null,
    original_record: null,
    delete_record: null,
    delete_reason: "",
    deleting: false,
    editing_bill_no: null,
    creating: false,
    bank_options: [],
  }),
  computed: {
    period_params() {
      return {
        semester: this.item ? this.item.semester : "-",
        year: this.item ? this.item.year : "-",
      };
    },
    can_edit() {
      return Boolean(
        this.editable &&
        this.details &&
        this.details.can_edit &&
        this.bank_options.length
      );
    },
    can_delete() {
      return Boolean(this.editable && this.details && this.details.can_delete);
    },
    can_create() {
      return Boolean(
        this.editable &&
        this.details &&
        this.details.can_create &&
        this.bank_options.length &&
        !this.details.billing.length &&
        !this.details.holding.length
      );
    },
    edit_summary() {
      if (!this.edit_record || !this.details) return 0;
      return this.details.fee_fields.reduce(
        (sum, field) => sum + (Number(this.edit_record[field]) || 0),
        0
      );
    },
    has_changes() {
      return this.creating ? this.edit_summary > 0 : this.changes.length > 0;
    },
    delete_reason_valid() {
      const length = this.delete_reason.trim().length;
      return length >= 3 && length <= 500;
    },
    changes() {
      if (!this.edit_record || !this.original_record || !this.details) return [];
      const fields = ["pay_date", "bank", ...this.details.visible_fee_fields];
      return fields
        .filter((field) => String(this.edit_record[field]) !== String(this.original_record[field]))
        .map((field) => {
          const isAmount = this.details.fee_fields.includes(field);
          return {
            field,
            label: field === "pay_date"
              ? this.$t("payment.payment_date")
              : field === "bank"
                ? this.$t("payment.bank")
                : this.fee_label(field),
            old_value: isAmount
              ? `${this.format_amount(this.original_record[field])} ${this.$t("payment.baht")}`
              : field === "pay_date"
                ? this.format_date(this.original_record[field])
                : field === "bank"
                  ? this.bank_display_name(
                    this.original_record[field],
                    this.original_record.bank_name
                  )
                  : this.original_record[field],
            new_value: isAmount
              ? `${this.format_amount(this.edit_record[field])} ${this.$t("payment.baht")}`
              : field === "pay_date"
                ? this.format_date(this.edit_record[field])
                : field === "bank"
                  ? this.bank_display_name(this.edit_record[field])
                  : this.edit_record[field],
          };
        });
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) this.fetch_details();
      else this.reset_dialogs();
    },
  },
  methods: {
    translated_error(error) {
      return typeof error === "object"
        ? objectTranslatorLowerCase(error, "message")
        : error;
    },
    fee_label(field) {
      const key = `payment.billing_field_${field}`;
      const translated = this.$t(key);
      return translated === key ? field : translated;
    },
    bank_display_name(bankCode, fallbackName = "") {
      const option = this.bank_options.find(
        (item) => String(item.bank_code) === String(bankCode)
      );
      return option?.bank_name || fallbackName || bankCode || "-";
    },
    registration_type_label(registration) {
      const typeKey = `payment.registration_type_${String(
        registration.regis_type || ""
      ).toLowerCase()}`;
      const translatedType = this.$t(typeKey);
      const typeLabel = translatedType === typeKey
        ? registration.regis_type || "-"
        : translatedType;
      const transactionType = String(registration.tran_type || "").toLowerCase();
      if (!transactionType || transactionType === "r") return typeLabel;

      const transactionKey = `payment.registration_transaction_${transactionType}`;
      const translatedTransaction = this.$t(transactionKey);
      return translatedTransaction === transactionKey
        ? typeLabel
        : `${typeLabel} (${translatedTransaction})`;
    },
    format_amount(value) {
      return Number(value || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    parse_date(value) {
      if (!value) return null;
      const parsed = new Date(String(value).replace(" ", "T"));
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    },
    date_locale() {
      return this.$i18n.locale === "th"
        ? "th-TH-u-ca-buddhist"
        : "en-GB-u-ca-buddhist";
    },
    format_date(value) {
      const date = this.parse_date(value);
      if (!date) return value || "-";
      return new Intl.DateTimeFormat(this.date_locale(), {
        calendar: "buddhist",
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(date);
    },
    format_datetime(value) {
      const date = this.parse_date(value);
      if (!date) return value || "-";
      return new Intl.DateTimeFormat(this.date_locale(), {
        calendar: "buddhist",
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }).format(date);
    },
    reset_dialogs() {
      this.edit_dialog = false;
      this.confirm_dialog = false;
      this.delete_dialog = false;
      this.edit_record = null;
      this.original_record = null;
      this.delete_record = null;
      this.delete_reason = "";
      this.deleting = false;
      this.editing_bill_no = null;
      this.creating = false;
      this.load_error = null;
      this.bank_options = [];
    },
    async fetch_details(openCreate = true) {
      if (!this.item) return;
      this.loading = true;
      this.load_error = null;
      this.details = null;
      try {
        this.details = await payInSlipService.getPaymentBillingDetails({
          level_id: this.level_id,
          sub_level_id: this.sub_level_id,
          student_id: this.student_id,
          year: this.item.year,
          semester: this.item.semester,
        });
        if (this.details.can_edit || this.details.can_create) {
          try {
            this.bank_options = await payInSlipService.getPaymentBillingCodes(
              this.level_id
            );
            if (!this.bank_options.length)
              this.load_error = this.$t("payment.no_billing_codes_for_level");
          } catch (error) {
            this.bank_options = [];
            this.load_error =
              error.response?.data?.message ||
              error.message ||
              this.$t("payment.billing_codes_load_failed");
          }
        }
        // หากยังไม่มี Billing/Holding และผู้ใช้มีสิทธิ์บันทึก ให้เปิดแบบกรอกเดิมทันที
        // เพื่อให้ flow จากไอคอนคอลัมน์ "ชำระแล้ว" จบในครั้งเดียวเหมือนระบบเก่า
        if (openCreate && this.value && this.can_create)
          this.$nextTick(() => this.open_create());
      } catch (error) {
        this.load_error = error.response?.data?.message || error.message;
      } finally {
        this.loading = false;
      }
    },
    open_edit(record) {
      if (!this.can_edit) return;
      this.creating = false;
      this.original_record = JSON.parse(JSON.stringify(record));
      this.edit_record = JSON.parse(JSON.stringify(record));
      this.editing_bill_no = record.bill_no;
      this.edit_dialog = true;
    },
    open_create() {
      if (!this.can_create) return;
      const now = new Date();
      const payDate = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, "0"),
        String(now.getDate()).padStart(2, "0"),
      ].join("-");
      const record = {
        bill_no: null,
        pay_date: payDate,
        bank: this.bank_options[0]?.bank_code ?? null,
      };
      // แยก "ยังไม่มีค่าเดิม" จาก BANK_CODE = '' เพื่อแสดงตัวเลือก "ไม่มี" ในหน้ายืนยันด้วย
      const original = { bill_no: null, pay_date: "", bank: null };
      this.details.fee_fields.forEach((field) => {
        record[field] = this.create_default_amount(field);
        original[field] = 0;
      });
      this.creating = true;
      this.editing_bill_no = null;
      this.original_record = original;
      this.edit_record = record;
      this.edit_dialog = true;
    },
    create_default_amount(field) {
      const suggested = Number(this.details?.suggested_payment?.[field] || 0);
      if (!this.item) return suggested;

      // ค่าที่ getPaymentData คำนวณแล้วอาจต่างจากค่าดิบในตาราง เช่น pay_charge
      // และ late_charge หลังพิจารณาสิทธิ์ยกเว้น จึงใช้ค่าจากแถวหน้าหลักก่อน
      const item_field_map = {
        special_fee2: "special_fee2",
        thesis_fee: "thesis_fee",
        comp_fee: "compre_fee",
        qe_fee: "qe_fee",
        late_charge: "late_charge",
        pay_charge: "pay_charge",
      };
      if (item_field_map[field]) {
        const value = this.raw_amount(
          this.item[item_field_map[field]],
          field === "special_fee2"
        );
        return value === null ? suggested : value;
      }

      const tuition_field_by_regis_type = {
        S: "package_fee",
        M: "maintain_fee",
        D: "leave_fee",
      };
      if (tuition_field_by_regis_type[this.item.regis_type] === field) {
        const value = this.raw_amount(this.item.tuition_fee);
        return value === null ? suggested : value;
      }
      return suggested;
    },
    raw_amount(value, allowNegative = false) {
      if (value === null || value === undefined || value === "") return null;
      const amount = Number(String(value).replace(/,/g, ""));
      if (!Number.isFinite(amount)) return null;
      const integerAmount = Math.trunc(amount);
      return allowNegative ? integerAmount : Math.max(0, integerAmount);
    },
    close_edit() {
      if (this.saving) return;
      this.edit_dialog = false;
      this.confirm_dialog = false;
      this.edit_record = null;
      this.original_record = null;
      this.editing_bill_no = null;
      this.creating = false;
    },
    open_delete(record) {
      if (!this.can_delete || this.deleting) return;
      this.delete_record = JSON.parse(JSON.stringify(record));
      this.delete_reason = "";
      this.delete_dialog = true;
    },
    close_delete() {
      if (this.deleting) return;
      this.delete_dialog = false;
      this.delete_record = null;
      this.delete_reason = "";
    },
    async delete_payment_record() {
      if (!this.can_delete || !this.delete_reason_valid || !this.delete_record || this.deleting)
        return;
      this.deleting = true;
      try {
        await payInSlipService.deletePaymentBillingDetail({
          level_id: this.level_id,
          sub_level_id: this.sub_level_id,
          student_id: this.student_id,
          year: this.item.year,
          semester: this.item.semester,
          bill_no: this.delete_record.bill_no,
          reason: this.delete_reason.trim(),
        });
        this.$toasted.success(this.$t("payment.billing_delete_success"));
        this.delete_dialog = false;
        this.delete_record = null;
        this.delete_reason = "";
        await this.fetch_details(false);
        this.$emit("updated");
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        this.$toasted.error(
          this.translated_error(message) || this.$t("payment.billing_delete_failed"),
          { duration: 0 }
        );
      } finally {
        this.deleting = false;
      }
    },
    async save_record() {
      const canSave = this.creating ? this.can_create : this.can_edit;
      if (!canSave || !this.has_changes || this.saving) return;
      this.saving = true;
      try {
        const payload = {
          pay_date: this.edit_record.pay_date,
          bank: this.edit_record.bank,
        };
        this.details.fee_fields.forEach((field) => {
          payload[field] = Number(this.edit_record[field]);
        });
        const request = {
          level_id: this.level_id,
          sub_level_id: this.sub_level_id,
          student_id: this.student_id,
          year: this.item.year,
          semester: this.item.semester,
          details: payload,
        };
        if (this.creating) {
          await payInSlipService.createPaymentBillingDetail(request);
        } else {
          await payInSlipService.updatePaymentBillingDetail({
            ...request,
            bill_no: this.edit_record.bill_no,
          });
        }
        this.$toasted.success(
          this.$t(this.creating ? "payment.billing_create_success" : "payment.billing_update_success")
        );
        this.edit_dialog = false;
        this.confirm_dialog = false;
        this.edit_record = null;
        this.original_record = null;
        this.editing_bill_no = null;
        this.creating = false;
        await this.fetch_details();
        this.$emit("updated");
      } catch (error) {
        const message = error.response?.data?.message || error.message;
        this.$toasted.error(this.translated_error(message) || this.$t("payment.update_failed"), {
          duration: 0,
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.payment-dialog-title {
  word-break: normal;
  background: linear-gradient(90deg, #fff7f3 0%, #ffffff 75%);
}
.payment-dialog-heading {
  color: #2f2f2f;
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1.35;
}
.payment-dialog-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  font-size: 0.9rem;
  font-weight: 400;
}
::v-deep .payment-dialog-meta .v-chip__content {
  color: #5f5f5f;
}
.payment-section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #444;
  font-size: 1rem;
  font-weight: 700;
}
.billing-comparison-wrap {
  width: fit-content;
  max-width: 100%;
  margin: 0 auto;
  overflow-x: auto;
  border: 1px solid #dedede;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}
.billing-comparison-table {
  width: auto;
  min-width: 640px;
  border-collapse: separate;
  border-spacing: 0;
  color: #333;
  font-size: 0.92rem;
}
.billing-comparison-table th,
.billing-comparison-table td {
  height: 38px;
  padding: 6px 9px;
  border-right: 1px solid #e0e0e0;
  border-bottom: 1px solid #e0e0e0;
  vertical-align: middle;
}
.billing-comparison-table tr > :last-child {
  border-right: 0;
}
.billing-comparison-table tbody tr:last-child > * {
  border-bottom: 0;
}
.billing-comparison-table thead th {
  position: sticky;
  top: 0;
  z-index: 3;
  background: #ececec;
  text-align: center;
  font-weight: 700;
}
.billing-comparison-table tbody tr:nth-child(even) > * {
  background: #fffaf4;
}
.billing-comparison-table tbody tr:nth-child(odd) > * {
  background: #fff;
}
.billing-comparison-table .sequence-column,
.billing-comparison-table .sequence-cell {
  position: sticky;
  left: 0;
  z-index: 2;
  width: 46px;
  min-width: 46px;
  text-align: center;
}
.billing-comparison-table .fee-name-column,
.billing-comparison-table .fee-name-cell,
.billing-comparison-table tbody tr > th:nth-child(2) {
  position: sticky;
  left: 46px;
  z-index: 2;
  width: 190px;
  min-width: 190px;
  text-align: left;
}
.billing-comparison-table thead .sequence-column,
.billing-comparison-table thead .fee-name-column {
  z-index: 4;
}
.billing-record-column {
  min-width: 160px;
}
.billing-date-with-transaction {
  min-width: 280px;
  white-space: nowrap;
}
.billing-transaction-date {
  margin-top: 12px;
  color: #777;
  font-size: 0.78rem;
  font-weight: 400;
  line-height: 1.5;
  white-space: nowrap;
}
.registration-record-column {
  background: #fff0e8 !important;
}
.billing-record-number {
  color: #444;
  font-size: 0.92rem;
}
.billing-receipt-number {
  margin-top: 2px;
  color: #777;
  font-size: 0.8rem;
  font-weight: 500;
}
.amount-cell {
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.zero-amount {
  color: #aaa;
}
.registration-amount {
  color: #555;
  font-weight: 600;
}
.billing-meta-row > *,
.billing-total-row > *,
.billing-bank-row > *,
.billing-action-row > * {
  background: #f5f5f5 !important;
}
.billing-total-row > * {
  font-weight: 700;
}
.total-amount {
  color: #0e9bd3;
}
.registration-total-amount {
  color: #ef6c00;
}
.billing-bank-row > * {
  background: #e7f4ff !important;
}
.billing-record-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
}
.billing-comparison-table .comparison-summary-label {
  text-align: right !important;
}
.comparison-summary-label > div {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 7px;
  white-space: nowrap;
}
.delete-summary-table th {
  width: 38%;
}
.detail-table th { width: 60%; }
.total-row { background: #f5f5f5; }
.orange-change { color: #ff5722; font-weight: 700; }
.payment-entry-card {
  font-size: 1rem;
}
.payment-entry-content {
  overflow: visible;
}
@media (max-width: 600px) {
  .payment-dialog-title { padding: 18px 16px !important; }
  .payment-dialog-meta { display: grid; gap: 5px; }
  .billing-comparison-table .fee-name-column,
  .billing-comparison-table .fee-name-cell,
  .billing-comparison-table tbody tr > th:nth-child(2) {
    width: 180px;
    min-width: 180px;
  }
  .billing-record-column { min-width: 155px; }
}
</style>
